import { useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  FC,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ConfirmationDialog,
  SearchBar,
  SelectableList,
} from "../../../components";
import { ALL_RELEASE_CHANNELS_QUERY_KEY } from "../../../hooks/useAllExperiences";
import {
  useAllUserGroupsForACompany,
  USER_GROUPS_QUERY_KEY,
} from "../../../hooks/useAllUserGroupsForACompany";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { INCOMING_LICENSES_QUERY_KEY } from "../../experiences/hooks/useIncomingLicenses";
import { USERS_IN_GROUP_QUERY_KEY } from "../../groups/hooks/useUsersInGroup";
import { ALL_LICENSES_QUERY_KEY } from "../../licenses/hooks/useExternalLicenses";
import { ErrorDisplay } from "../AddNewUser/ErrorDisplay";
import { AddUserToGroupDialogProps } from "../AddNewUser/types";
import { USER_RELEASE_CHANNELS_QUERY_KEY } from "../hooks/useUserExperiences";

export const AddUserToGroupDialog: FC<AddUserToGroupDialogProps> = ({
  isOpen,
  onClose,
  companyId,
  userId,
  userGroupsToExclude,
}) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useLocalization();
  const isMobile = useIsMobile();
  const userManagementService = useUserManagementServiceV1();

  const [selectedUserGroupIds, setSelectedUserGroupIds] = useState<string[]>(
    [],
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const handleResetDialog = useCallback(() => {
    setSearchTerm("");
    setSelectedUserGroupIds([]);
  }, []);

  const addUserToGroupMutation = useMutation({
    mutationFn: async (userGroupIds: string[]) => {
      if (!userId) {
        throw new Error("User ID is required");
      }

      return await Promise.all(
        userGroupIds.map((userGroupId) =>
          userManagementService.addUserToUserGroup(userGroupId, userId),
        ),
      );
    },
    onSuccess: () => {
      const addedGroups = filteredGroups.filter((group) =>
        selectedUserGroupIds.includes(group.userGroupId),
      );

      queryClient.setQueryData(
        [USER_GROUPS_QUERY_KEY, userId],
        (oldData: UserManagement_V1.UserGroup[] | undefined) => [
          ...(oldData ?? []),
          ...addedGroups,
        ],
      );

      void queryClient.invalidateQueries({ queryKey: [USER_GROUPS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [USER_RELEASE_CHANNELS_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [INCOMING_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_LICENSES_QUERY_KEY],
      });
      void queryClient.invalidateQueries({
        queryKey: [ALL_RELEASE_CHANNELS_QUERY_KEY],
      });

      for (const group of addedGroups) {
        void queryClient.invalidateQueries({
          queryKey: [USERS_IN_GROUP_QUERY_KEY, group.userGroupId],
        });
      }

      onClose();
    },
  });

  useEffect(() => {
    if (!isOpen) {
      handleResetDialog();
      addUserToGroupMutation.reset();
    }
  }, [isOpen, handleResetDialog, addUserToGroupMutation.reset]);

  const { groups: allGroups = [], isLoading } =
    useAllUserGroupsForACompany(companyId, true);

  const filteredGroups = useMemo(() => {
    return allGroups.filter(
      (group) =>
        !userGroupsToExclude?.some(
          (excludedGroup) => excludedGroup.userGroupId === group.userGroupId,
        ),
    );
  }, [allGroups, userGroupsToExclude]);

  const mappedGroups = useMemo(() => {
    return filteredGroups.map((group) => ({
      id: group.userGroupId,
      primaryText: group.userGroupName,
      secondaryText: t("users.usersCount", { count: group.userCount }),
    }));
  }, [filteredGroups, t]);

  const selectableItems = useMemo(() => {
    if (deferredSearchTerm.trim()) {
      return mappedGroups.filter((group) =>
        group.primaryText
          .toLowerCase()
          .includes(deferredSearchTerm.toLowerCase()),
      );
    }

    return mappedGroups;
  }, [mappedGroups, deferredSearchTerm]);

  const selectedItems = useMemo(() => {
    return mappedGroups.filter((group) =>
      selectedUserGroupIds.includes(group.id),
    );
  }, [mappedGroups, selectedUserGroupIds]);

  const handleUserGroupSelect = useCallback((userGroupIdToSelect: string) => {
    setSelectedUserGroupIds((prev) => {
      if (prev.includes(userGroupIdToSelect)) {
        return prev.filter((id) => id !== userGroupIdToSelect);
      }

      return [...prev, userGroupIdToSelect];
    });
  }, []);

  const handleChipRemove = useCallback((userGroupIdToRemove: string) => {
    setSelectedUserGroupIds((prev) =>
      prev.filter((id) => id !== userGroupIdToRemove),
    );
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchTerm("");
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  const handleCancel = () => {
    if (addUserToGroupMutation.isPending) {
      return;
    }
    onClose();
  };

  const handleSubmit = () => {
    if (selectedUserGroupIds.length > 0) {
      addUserToGroupMutation.mutate(selectedUserGroupIds);
    }
  };

  const addButtonText = useMemo(() => {
    if (addUserToGroupMutation.isPending) {
      return t("users.addUserDialog.adding");
    }
    if (isMobile) {
      return t("users.addUserDialog.add");
    }
    if (selectedUserGroupIds.length === 0) {
      return t("users.addUserDialog.title");
    }
    if (selectedUserGroupIds.length === 1) {
      return t("users.addUserDialog.addToOneGroup");
    }

    return t("users.addUserDialog.addToMultipleGroups", {
      count: selectedUserGroupIds.length,
    });
  }, [
    addUserToGroupMutation.isPending,
    isMobile,
    selectedUserGroupIds.length,
    t,
  ]);

  return (
    <ConfirmationDialog.Root
      aria-describedby="add-user-to-group-description"
      aria-labelledby="add-user-to-group-title"
      fullScreenOnMobile={true}
      fullWidth
      height="728px"
      maxWidth="xs"
      onClose={handleCancel}
      open={isOpen}
    >
      <ConfirmationDialog.Title id="add-user-to-group-title" showIcon={false}>
        {t("users.addUserDialog.title")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
          <SearchBar
            handleClear={handleSearchClear}
            handleSubmit={handleSearchSubmit}
            onChange={handleSearchChange}
            placeholder={t("users.addUserDialog.searchPlaceholder")}
            sx={{ maxWidth: undefined }}
            value={searchTerm}
          />
        </form>
        <SelectableList
          disabled={addUserToGroupMutation.isPending}
          emptyMessage={t("users.addUserDialog.noGroupsAvailable")}
          height="100%"
          isLoading={isLoading}
          items={selectableItems}
          maxHeight="100%"
          minHeight={0}
          multiSelect={true}
          onChipRemove={handleChipRemove}
          onItemSelect={handleUserGroupSelect}
          selectedIds={selectedUserGroupIds}
          selectedItems={selectedItems}
          selectedItemsTitle={t("users.addUserDialog.selectedGroups")}
          sx={{ flex: 1, minHeight: 0 }}
        />
        {addUserToGroupMutation.error && (
          <ErrorDisplay error={addUserToGroupMutation.error} />
        )}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions>
        <ConfirmationDialog.Button
          disabled={addUserToGroupMutation.isPending}
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("users.addUserDialog.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={
            selectedUserGroupIds.length === 0 ||
            addUserToGroupMutation.isPending
          }
          onClick={handleSubmit}
          variant="contained"
        >
          {addButtonText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
